"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import FlightPassengersAndCabinClass from "./FlightPassengersAndCabinClass";
import AutoComplete from "@/components/flightSearchSection/AutoComplete";
import DatePicker from "@/components/flightSearchSection/DatePicker";
import { useModalHelpers } from "@/contexts/ModalContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { formatDate } from "@/utils/dateFormatter";

const MultiCityFlights = ({
  multiFlights,
  addMultiFlight,
  removeMultiFlight,
  updateMultiFlight,
  toggleMultiFlightCities,
  searchForFromCity,
  latestFlightSearches,
  popularCities,
  nearestAirports,
  passengers,
  cabinClass,
  setPassengersAndCabinClass,
  onlyDirectFlights,
  setOnlyDirectFlights,
  publicHolidays,
  defaultMonth,
  viewportHeightWrapperActive = false,
  hasDatePickerTabs = true,
  onSearch,
  passengersDisabled = false,
  citiesDisabled = false,
  initialLoading = false,
}) => {
  const t = useTranslationDetector();
  const tForm = useTranslationDetector("flightSearch");
  const router = useRouter();
  const { errorModal } = useModalHelpers();
  const [validationErrors, setValidationErrors] = useState({});

  // Her uçuş için ayrı date picker ref'leri
  const datePickerRefs = useRef({});

  const handleCitySelect = useCallback(
    (flightId, field, city) => {
      updateMultiFlight(flightId, field, city);

      // Eğer "arrivalCity" (nereye) seçildiyse, bir sonraki uçuşun "departureCity"sini güncelle
      if (field === "arrivalCity") {
        const currentFlightIndex = multiFlights.findIndex(
          (f) => f.id === flightId,
        );
        const nextFlight = multiFlights[currentFlightIndex + 1];

        if (nextFlight) {
          updateMultiFlight(nextFlight.id, "departureCity", city);
          // Bir sonraki uçuşun departureCity hatasını da temizle
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`${flightId}-${field}`];
            delete newErrors[`${nextFlight.id}-departureCity`];
            return newErrors;
          });
        } else {
          // Sadece mevcut alan hatasını temizle
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`${flightId}-${field}`];
            return newErrors;
          });
        }
      } else {
        // Diğer alanlar için sadece o alanın hatasını temizle
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`${flightId}-${field}`];
          return newErrors;
        });
      }
    },
    [updateMultiFlight, multiFlights],
  );

  // Son arama seçildiğinde hem nereden hem nereye alanlarını doldur
  const handleLatestSearchSelect = useCallback(
    (flightId, latestSearchItem) => {
      // Hem departureCity hem arrivalCity'yi set et
      updateMultiFlight(
        flightId,
        "departureCity",
        latestSearchItem.departureCity,
      );
      updateMultiFlight(flightId, "arrivalCity", latestSearchItem.returnCity);

      // Sonraki uçuşun departureCity'sini de güncelle
      const currentFlightIndex = multiFlights.findIndex(
        (f) => f.id === flightId,
      );
      const nextFlight = multiFlights[currentFlightIndex + 1];

      if (nextFlight) {
        updateMultiFlight(
          nextFlight.id,
          "departureCity",
          latestSearchItem.returnCity,
        );
      }

      // Validation hatalarını temizle
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${flightId}-departureCity`];
        delete newErrors[`${flightId}-arrivalCity`];
        if (nextFlight) {
          delete newErrors[`${nextFlight.id}-departureCity`];
        }
        return newErrors;
      });
    },
    [updateMultiFlight, multiFlights],
  );

  const handleDateSelect = useCallback(
    (flightId, date) => {
      updateMultiFlight(flightId, "departureDate", date);

      // Tarih seçim hatasını temizle
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${flightId}-departureDate`];

        // Bu uçuşun tarihi değiştiyse, sonraki uçuşların tarih validasyonlarını da kontrol et
        const currentFlightIndex = multiFlights.findIndex(
          (f) => f.id === flightId,
        );

        // Sonraki uçuşların tarih hatalarını temizle (yeniden validasyon yapılacak)
        for (let i = currentFlightIndex + 1; i < multiFlights.length; i++) {
          const nextFlightId = multiFlights[i].id;
          const errorKey = `${nextFlightId}-departureDate`;
          // Eğer bu hata "departureDateBeforePrevious" hatasıysa temizle
          if (newErrors[errorKey]) {
            delete newErrors[errorKey];
          }
        }

        return newErrors;
      });
    },
    [updateMultiFlight, multiFlights],
  );

  const handleRemoveFlight = useCallback(
    (flightId) => {
      // Minimum 2 uçuş kontrolü
      if (multiFlights.length <= 2) {
        return;
      }

      removeMultiFlight(flightId);

      // Bu uçuşla ilgili validasyon hatalarını temizle
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`${flightId}-`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    },
    [multiFlights.length, removeMultiFlight],
  );

  const handleAddFlight = useCallback(() => {
    if (multiFlights.length >= 3) {
      return;
    }

    addMultiFlight();
  }, [multiFlights.length, addMultiFlight]);

  const handleToggleCities = useCallback(
    (flightId) => {
      toggleMultiFlightCities(flightId);
    },
    [toggleMultiFlightCities],
  );

  const validateMultiFlights = useCallback(() => {
    const errors = {};

    multiFlights.forEach((flight, index) => {
      if (!flight.departureCity) {
        errors[`${flight.id}-departureCity`] = tForm("departureCityRequired");
      }
      if (!flight.arrivalCity) {
        errors[`${flight.id}-arrivalCity`] = tForm("arrivalCityRequired");
      }
      if (!flight.departureDate) {
        errors[`${flight.id}-departureDate`] = tForm("departureDateRequired");
      }

      // Aynı şehir kontrolü
      if (
        flight.departureCity &&
        flight.arrivalCity &&
        flight.departureCity.code === flight.arrivalCity.code
      ) {
        errors[`${flight.id}-arrivalCity`] = tForm("sameCity");
      }

      // Tarih kontrolü: Bir önceki uçuştan önce olamaz
      if (index > 0 && flight.departureDate) {
        const previousFlight = multiFlights[index - 1];
        if (previousFlight?.departureDate) {
          const currentDate = new Date(flight.departureDate);
          const previousDate = new Date(previousFlight.departureDate);

          // Tarihleri sadece tarih kısmına göre karşılaştır (saat bilgisini göz ardı et)
          currentDate.setHours(0, 0, 0, 0);
          previousDate.setHours(0, 0, 0, 0);

          if (currentDate < previousDate) {
            errors[`${flight.id}-departureDate`] = tForm(
              "departureDateBeforePrevious",
            );
          }
        }
      }
    });

    // Hata varsa errorModal göster
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors);
      const firstError = errorMessages[0];

      errorModal({
        title: t("common.defaultErrorTitle"),
        message: firstError,
        size: "sm",
        showCancel: false,
        isBottomSheet: true,
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [multiFlights, tForm, errorModal, t]);

  const generateMultiCityUrl = useCallback(() => {
    // URL segments oluştur: havalimani-isimleri/IATA-kodlari/tarih
    const segments = multiFlights
      .map((flight) => {
        const fromSlug = flight.departureCity?.id
          ?.toLowerCase()
          .replace(/\s+/g, "-");
        const toSlug = flight.arrivalCity?.id
          ?.toLowerCase()
          .replace(/\s+/g, "-");
        // const fromCode = flight.departureCity?.code;
        // const toCode = flight.arrivalCity?.code;
        const date = formatDate(flight.departureDate, { format: "YYYY-MM-DD" });

        return `${fromSlug}-${toSlug}/${date}`;
        // return `${fromSlug}-${toSlug}/${fromCode}-${toCode}/${date}`;
      })
      .join("/");

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Mevcut URL'deki campaignId'yi kontrol et ve varsa ekle
    const currentParams = new URLSearchParams(window.location.search);
    const campaignId = currentParams.get("campaignId");
    if (campaignId) {
      queryParams.set("campaignId", campaignId);
    }

    // adult her zaman (0 dahil) — tek/çift yön ile aynı davranış
    queryParams.set("adult", String(passengers.adult));
    if (passengers.student > 0)
      queryParams.set("student", passengers.student.toString());
    if (passengers.child > 0)
      queryParams.set("child", passengers.child.toString());
    if (passengers.infant > 0)
      queryParams.set("infant", passengers.infant.toString());
    if (passengers.elderly > 0)
      queryParams.set("elderly", passengers.elderly.toString());

    // Kabin sınıfını ekle
    if (cabinClass && cabinClass !== "economy") {
      queryParams.set("class", cabinClass);
    }

    // Multi-city trip type
    queryParams.set("tripType", "multi-city");

    // Direct flights seçeneğini ekle
    if (onlyDirectFlights) {
      queryParams.set("directOnly", "true");
    }

    const queryString = queryParams.toString();
    const baseUrl = `/flight/multi-city/${segments}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [multiFlights, passengers, cabinClass, onlyDirectFlights]);

  const handleMultiCitySearch = useCallback(() => {
    const isValid = validateMultiFlights();
    if (isValid && onSearch) {
      const segments = multiFlights.map((flight) => ({
        origin: flight.departureCity?.code ?? flight.departureCity?.id,
        destination: flight.arrivalCity?.code ?? flight.arrivalCity?.id,
        originIsCity: flight.departureCity?.isCity ?? true,
        destinationIsCity: flight.arrivalCity?.isCity ?? true,
        departureDate: formatDate(flight.departureDate, {
          format: "YYYY-MM-DD",
        }),
      }));
      onSearch({
        tripMode: "multi-city",
        segments,
        passengers,
        cabinClass,
        onlyDirectFlights,
      });
      return;
    }
    if (isValid) {
      const multiCityUrl = generateMultiCityUrl();

      router.push(multiCityUrl, undefined, {
        scroll: true,
      });
    }
  }, [
    validateMultiFlights,
    generateMultiCityUrl,
    multiFlights,
    cabinClass,
    passengers,
    onlyDirectFlights,
    router,
    onSearch,
  ]);

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="flex flex-col gap-6 md:gap-4">
        {multiFlights.map((flight, index) => (
          <div
            key={flight.id}
            className={clsx("relative flex items-center justify-between gap-4")}
            style={{
              zIndex: multiFlights.length - index,
            }}
          >
            <div className="flex w-full flex-col items-center gap-4 md:flex-row">
              <div className="flex w-full items-center justify-between md:hidden">
                <span className="text-lg font-medium">{`${tForm("flight")} ${index + 1}`}</span>
                <button
                  onClick={() => handleRemoveFlight(flight.id)}
                  disabled={multiFlights.length <= 2}
                  className="text-error hover:text-error/80 h-8 w-8 cursor-pointer items-center justify-center rounded-full text-lg font-medium transition-colors disabled:cursor-not-allowed disabled:text-gray-300 md:flex"
                  title={
                    multiFlights.length <= 2
                      ? t("flight.minimumFlights")
                      : t("flight.removeFlight")
                  }
                >
                  <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} />
                </button>
              </div>
              <div className="shadow-medium flex w-full flex-col rounded-xl bg-white md:flex-row md:items-center">
                <div className="flex flex-1 flex-col items-center md:flex-row">
                  <div className="relative z-[1] flex w-full flex-col items-end md:static md:flex-row md:items-center">
                    <div className="w-full md:w-1/2">
                      <AutoComplete
                        id={`multi-from-${flight.id}`}
                        label={tForm("from")}
                        searchFunction={searchForFromCity}
                        onSelect={(city) =>
                          handleCitySelect(flight.id, "departureCity", city)
                        }
                        onSelectLatestSearch={(latestSearchItem) =>
                          handleLatestSearchSelect(flight.id, latestSearchItem)
                        }
                        isFromDate={true}
                        isMultiCity={true}
                        latestFlightSearches={latestFlightSearches}
                        popularCities={popularCities}
                        nearestAirports={nearestAirports}
                        value={flight.departureCity?.value}
                        error={validationErrors[`${flight.id}-departureCity`]}
                        viewportHeightWrapperActive={
                          viewportHeightWrapperActive
                        }
                        disabled={citiesDisabled}
                        isInitialLoading={initialLoading}
                      />
                    </div>

                    {!citiesDisabled && (
                      <span className="relative z-[11] h-[1px] w-[1px] overflow-visible md:h-6 md:w-6">
                        <button
                          onClick={() => handleToggleCities(flight.id)}
                          className="border-info-weaker flex h-11 w-11 -translate-x-[52px] -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border-2 bg-white md:-translate-x-[10px] md:-translate-y-[10px]"
                        >
                          <FontAwesomeIcon
                            icon={byPrefixAndName.far["arrow-up-arrow-down"]}
                            className="md:rotate-90"
                          />
                        </button>
                      </span>
                    )}

                    <div className="w-full md:w-1/2">
                      <AutoComplete
                        id={`multi-to-${flight.id}`}
                        label={tForm("to")}
                        searchFunction={searchForFromCity}
                        onSelect={(city) =>
                          handleCitySelect(flight.id, "arrivalCity", city)
                        }
                        onSelectLatestSearch={(latestSearchItem) =>
                          handleLatestSearchSelect(flight.id, latestSearchItem)
                        }
                        isFromDate={false}
                        isMultiCity={true}
                        latestFlightSearches={latestFlightSearches}
                        popularCities={popularCities}
                        nearestAirports={nearestAirports}
                        value={flight.arrivalCity?.value}
                        error={validationErrors[`${flight.id}-arrivalCity`]}
                        viewportHeightWrapperActive={
                          viewportHeightWrapperActive
                        }
                        disabled={citiesDisabled}
                        isInitialLoading={initialLoading}
                      />
                    </div>
                  </div>
                </div>

                <span className="bg-border mx-1 hidden h-[34px] w-[1px] md:block"></span>

                <div className="w-full md:w-1/2">
                  <DatePicker
                    ref={(ref) => {
                      if (ref) {
                        datePickerRefs.current[flight.id] = ref;
                      }
                    }}
                    id={`multi-date-${flight.id}`}
                    label={tForm("departureDate")}
                    onSelect={(date) => handleDateSelect(flight.id, date)}
                    value={flight?.departureDate}
                    departureDate={flight?.departureDate}
                    isFromDate={true}
                    isMultiCity={true}
                    publicHolidays={publicHolidays}
                    defaultMonth={defaultMonth}
                    viewportHeightWrapperActive={viewportHeightWrapperActive}
                    hasDatePickerTabs={hasDatePickerTabs}
                    disabled={
                      index > 0 && multiFlights[index - 1]?.departureDate
                        ? (() => {
                            const today = new Date();
                            const prevDate = new Date(
                              multiFlights[index - 1].departureDate,
                            );
                            today.setHours(0, 0, 0, 0);
                            prevDate.setHours(0, 0, 0, 0);
                            return prevDate > today ? prevDate : today;
                          })()
                        : new Date()
                    }
                  />
                </div>
              </div>
              <button
                onClick={() => handleRemoveFlight(flight.id)}
                disabled={multiFlights.length <= 2}
                className="text-error hover:text-error/80 hidden h-8 w-8 cursor-pointer items-center justify-center rounded-full text-lg font-medium transition-colors disabled:cursor-not-allowed disabled:text-gray-300 md:flex"
                title={
                  multiFlights.length <= 2
                    ? t("flight.minimumFlights")
                    : t("flight.removeFlight")
                }
              >
                <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} />
              </button>
            </div>
          </div>
        ))}

        {multiFlights.length < 3 && (
          <div className="flex w-full justify-start">
            <button
              onClick={handleAddFlight}
              className="text-primary flex w-auto cursor-pointer items-center justify-center gap-2 text-lg font-medium"
            >
              <FontAwesomeIcon icon={byPrefixAndName.far["circle-plus"]} />
              <span>{t("flight.addFlight")}</span>
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-4 md:gap-6">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row md:pr-11">
            <FlightPassengersAndCabinClass
              label={tForm("passengerAndClass")}
              value={{ passengers, cabinClass }}
              onSelect={setPassengersAndCabinClass}
              isMultiTrip={true}
              viewportHeightWrapperActive={viewportHeightWrapperActive}
              passengersDisabled={passengersDisabled}
            />
            <button
              id="flight-search-button"
              type="button"
              onClick={handleMultiCitySearch}
              className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full min-w-50 cursor-pointer items-center justify-center gap-2 rounded-lg p-4 text-lg font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] md:w-auto"
            >
              <span>{t("flight.search")}</span>
              <FontAwesomeIcon
                className="hidden text-[28px] md:block"
                icon={byPrefixAndName.fak["turna-logo-icon"]}
              />
            </button>
          </div>
          <label className="inline-flex w-auto cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={onlyDirectFlights}
              onChange={(e) => setOnlyDirectFlights(e.target.checked)}
              className="text-primary border-border-strong checked:border-primary ring-none h-6 w-6 rounded-[6px] border-[1.2px] outline-0 transition-all duration-200 ease-in-out focus:ring-transparent"
            />
            <span>{t("flight.onlyDirectFlights")}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default MultiCityFlights;
